import { inject, Injectable } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { GqlService, PatientGroupType } from '@shared/services/gql.service';
import { MainQuery } from '@shared/store/main/main.query';
import { Option } from '@shared/types/components.type';
import { Utils } from '@shared/utils/utils';

import { PatientGroupsModel } from './patient-groups.model';
import { PatientGroupsStore } from './patient-groups.store';

@Injectable({ providedIn: 'root' })
export class PatientGroupsService {
  private gqlService = inject(GqlService);
  private mainQuery = inject(MainQuery);
  private patientGroupsStore = inject(PatientGroupsStore);

  async add({
    description,
    name,
    rank_order,
    type,
  }: {
    description: null | string;
    name: string;
    rank_order: null | number;
    type: PatientGroupType;
  }) {
    const { data, errors, success } = await firstValueFrom(
      this.gqlService.createPatientGroup$({ description, name, rank_order, type })
    );
    let patientGroup: null | PatientGroupsModel = null;

    if (success && data) {
      patientGroup = {
        ...data,
        curveOnly: data.type === PatientGroupType.PATIENT_GROUP_FORECAST_ONLY,
      } as PatientGroupsModel;

      this.patientGroupsStore.add(patientGroup);
    }

    return { data: patientGroup, errors, success };
  }

  get(patientGroupTypes?: PatientGroupType[]) {
    return this.mainQuery.select('trialKey').pipe(
      switchMap(() => {
        this.patientGroupsStore.setLoading(true);

        return this.gqlService
          .listPatientGroups$(
            patientGroupTypes || [
              PatientGroupType.PATIENT_GROUP_FORECAST_ONLY,
              PatientGroupType.PATIENT_GROUP_STANDARD,
            ]
          )
          .pipe(
            map(({ data, errors, success }) => {
              let patientGroups = [] as PatientGroupsModel[];

              if (data) {
                patientGroups = data.map((patientGroup) => {
                  return {
                    ...patientGroup,
                    curveOnly: patientGroup.type === PatientGroupType.PATIENT_GROUP_FORECAST_ONLY,
                  } as PatientGroupsModel;
                });
                this.patientGroupsStore.set(patientGroups);
              }

              this.patientGroupsStore.setLoading(false);

              return { data: patientGroups, errors, success };
            })
          );
      })
    );
  }

  getPatientGroupOptions$(): Observable<Option[]> {
    return this.gqlService
      .patientGroupsOption$([PatientGroupType.PATIENT_GROUP_STANDARD])
      .pipe(
        map(({ data }) =>
          (data || [])
            .sort(({ rank_order }, { rank_order: rank_order2 }) =>
              Utils.alphaNumSort(rank_order ?? -1, rank_order2 ?? -1)
            )
            .map(({ id, name }) => ({ label: name, value: id }))
        )
      );
  }

  async remove(patientGroup: PatientGroupsModel) {
    const { errors, success } = await firstValueFrom(
      this.gqlService.removePatientGroup$(patientGroup.id)
    );

    if (success) {
      this.patientGroupsStore.remove(patientGroup.id);
    }

    return { errors, success };
  }

  async update(patientGroup: PatientGroupsModel) {
    const { data, errors, success } = await firstValueFrom(
      this.gqlService.updatePatientGroup$({
        description: patientGroup.description,
        id: patientGroup.id,
        name: patientGroup.name,
        rank_order: patientGroup.rank_order,
        type: patientGroup.type,
      })
    );

    if (success && data) {
      this.patientGroupsStore.update(patientGroup.id, {
        curveOnly: patientGroup.type === PatientGroupType.PATIENT_GROUP_FORECAST_ONLY,
        description: patientGroup.description,
        name: patientGroup.name,
        rank_order: patientGroup.rank_order,
        type: patientGroup.type,
      });
    }

    return { data, errors, success };
  }
}
